import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { apiFetch } from '@/lib/marketing/api';
import { toast } from '@/hooks/use-toast';

const localStorageKey = (main: string) => `sovereign_wallets_${main.toLowerCase()}`;
const migrationDoneKey = (main: string) => `sovereign_wallets_migrated_${main.toLowerCase()}`;

export interface ManagedWallet {
  address: string;
  label: string;
}

function loadLocal(mainAddress: string): ManagedWallet[] {
  try {
    const raw = localStorage.getItem(localStorageKey(mainAddress));
    const parsed: ManagedWallet[] = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocal(mainAddress: string, list: ManagedWallet[]) {
  localStorage.setItem(localStorageKey(mainAddress), JSON.stringify(list));
}

async function fetchServerWallets(): Promise<ManagedWallet[] | null> {
  try {
    const data = await apiFetch<{ wallets: ManagedWallet[] }>('/api/v1/user/wallets');
    return data.wallets ?? null;
  } catch {
    return null;
  }
}

async function migrateToServer(localWallets: ManagedWallet[]): Promise<boolean> {
  try {
    const data = await apiFetch<{ success: boolean }>('/api/v1/user/wallets/sync', {
      method: 'POST',
      body: JSON.stringify({ wallets: localWallets }),
    });
    return data.success;
  } catch {
    return false;
  }
}

// Single-wallet POST, reused by addWallet and by the sync flush of unsynced
// local wallets. Rejects on failure so callers can react.
function postWallet(w: ManagedWallet): Promise<unknown> {
  return apiFetch('/api/v1/user/wallets', {
    method: 'POST',
    body: JSON.stringify({ address: w.address, label: w.label || undefined }),
  });
}

export function useWalletList(mainAddress: string | undefined) {
  const [additionalWallets, setAdditionalWallets] = useState<ManagedWallet[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const syncInProgress = useRef(false);

  const syncWithServer = useCallback(async (address: string) => {
    if (syncInProgress.current) return;
    syncInProgress.current = true;
    try {
      const serverWallets = await fetchServerWallets();
      if (serverWallets === null) {
        setIsAuthenticated(false);
        return;
      }
      setIsAuthenticated(true);

      if (serverWallets.length > 0) {
        // Merge, don't overwrite: keep any local wallets the server doesn't have
        // yet (an add whose POST failed, or one made before auth settled) and
        // flush them up so a failed/racy save is never silently lost.
        const serverAddrs = new Set(serverWallets.map((w) => w.address.toLowerCase()));
        const pending = loadLocal(address).filter((w) => !serverAddrs.has(w.address.toLowerCase()));
        if (pending.length > 0) {
          await Promise.all(pending.map((w) => postWallet(w).catch(() => {})));
        }
        const merged = [...serverWallets, ...pending];
        setAdditionalWallets(merged);
        saveLocal(address, merged);
      } else {
        const localData = loadLocal(address);
        const alreadyMigrated = localStorage.getItem(migrationDoneKey(address));
        if (localData.length > 0 && !alreadyMigrated) {
          const ok = await migrateToServer(localData);
          if (ok) {
            localStorage.removeItem(localStorageKey(address));
            localStorage.setItem(migrationDoneKey(address), '1');
          }
        }
      }
    } finally {
      syncInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    if (!mainAddress) { setAdditionalWallets([]); return; }
    setAdditionalWallets(loadLocal(mainAddress));
    syncWithServer(mainAddress);
  }, [mainAddress, syncWithServer]);

  const addWallet = useCallback((address: string, label?: string) => {
    if (!mainAddress) return false;
    const addr = address.trim().toLowerCase();
    if (!addr.startsWith('0x') || addr.length !== 42) return false;
    if (addr === mainAddress.toLowerCase()) return false;
    // Pre-check against current state so the isAuthenticated branch runs
    // synchronously (React calls setState updaters during render, not immediately).
    if (additionalWallets.some((w) => w.address === addr)) return false;

    const newLabel = label?.trim() || '';

    setAdditionalWallets((prev) => {
      if (prev.some((w) => w.address === addr)) return prev; // safety check
      const finalLabel = newLabel || `Wallet ${prev.length + 1}`;
      const next = [...prev, { address: addr, label: finalLabel }];
      saveLocal(mainAddress, next);
      return next;
    });

    if (isAuthenticated) {
      postWallet({ address: addr, label: newLabel })
        .catch((err) => {
          Sentry.captureException(err, { tags: { feature: 'wallet-add' }, extra: { wallet: addr } });
          toast({
            title: "Couldn't save wallet to the server",
            description: 'Kept locally — it will retry automatically on your next refresh.',
            variant: 'destructive',
          });
        });
    }
    return true;
  }, [mainAddress, isAuthenticated, additionalWallets]);

  const removeWallet = useCallback((address: string) => {
    if (!mainAddress) return;
    const addr = address.toLowerCase();
    setAdditionalWallets((prev) => {
      const next = prev.filter((w) => w.address !== addr);
      saveLocal(mainAddress, next);
      return next;
    });

    if (isAuthenticated) {
      apiFetch(`/api/v1/user/wallets/${addr}`, { method: 'DELETE' }).catch(() => {});
    }
  }, [mainAddress, isAuthenticated]);

  const renameWallet = useCallback((address: string, label: string) => {
    if (!mainAddress) return;
    const addr = address.toLowerCase();
    setAdditionalWallets((prev) => {
      const next = prev.map((w) => w.address === addr ? { ...w, label } : w);
      saveLocal(mainAddress, next);
      return next;
    });

    if (isAuthenticated) {
      apiFetch(`/api/v1/user/wallets/${addr}`, {
        method: 'PATCH',
        body: JSON.stringify({ label }),
      }).catch(() => {});
    }
  }, [mainAddress, isAuthenticated]);

  return { additionalWallets, addWallet, removeWallet, renameWallet };
}
